import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { RequirementService } from 'src/app/services/requirement.service';
import { AiService } from 'src/app/services/ai.service';


@Component({
    selector: 'app-prompts-ai',
    templateUrl: './prompts-ai.component.html',
    styleUrls: ['./prompts-ai.component.scss'],
    standalone: false
})
export class PromptsAiComponent implements OnInit, OnDestroy {
	@Input() requirementId: number;
	@Input() summaryData: {
		summary: string;
		relevantData: any;
	} = {
		summary: 'Sin resumen',
		relevantData: {}
	};
	@Input() titleModal: string;
	submitted = false;
	analisys_submitted = false;
	prompt = 'Generar un informe que pueda enviar por correo para mi cliente sobre el estado de este requerimiento.';
	promptResult = '';
	/** Error real del análisis (failed / transporte / cuota). No usar para estado "procesando". */
	analysisError: string | null = null;
	/** Info mientras analyze + backgroundprocess están pending/processing (alerta azul, no danger). */
	analysisProgress: string | null = null;

	private pollSubscription: Subscription | null = null;
	private pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
	/**
	 * Generación del poll activo. Al reanalizar/cerrar se incrementa para ignorar
	 * respuestas de polls anteriores (evita que un failed viejo pise un process nuevo).
	 */
	private pollGeneration = 0;

	constructor(
		public activeModal: NgbActiveModal,
		public _requirements: RequirementService,
		private aiService: AiService,
	) { }

	ngOnInit() {
		this.submitted = false;
	}

	ngOnDestroy() {
		this.stopBackgroundPolling();
	}

	private setAnalysisError(message: string, processPayload?: any) {
		this.analisys_submitted = false;
		this.analysisProgress = null;
		this.analysisError = this.resolveAnalysisErrorMessage(message, processPayload);
	}

	/** Marca UI en progreso: limpia error previo y mantiene el spinner "Analizando...". */
	private setAnalysisProgress(message: string) {
		this.analysisError = null;
		this.analisys_submitted = true;
		this.analysisProgress = message;
	}

	/** Cancela timeout/subscribe del poll e invalida callbacks en vuelo. */
	private stopBackgroundPolling() {
		this.pollGeneration++;
		if (this.pollTimeoutId != null) {
			clearTimeout(this.pollTimeoutId);
			this.pollTimeoutId = null;
		}
		if (this.pollSubscription) {
			this.pollSubscription.unsubscribe();
			this.pollSubscription = null;
		}
	}

	/**
	 * El routeReader envuelve el handler como `{ result: { status, error, result } }`.
	 * El handler de backgroundprocess devuelve `{ status, error, result }` y `result` es text JSON.
	 * Si llegara sin wrap, `resp.error` sería string y no hay que tratarlo como error de transporte.
	 */
	private normalizeBackgroundProcess(resp: any): { status?: string; error?: any; result?: any } | null {
		if (!resp || typeof resp !== 'object') {
			return null;
		}

		const wrapped = resp.result;
		if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped) && 'status' in wrapped) {
			return wrapped;
		}

		if ('status' in resp) {
			return resp;
		}

		return null;
	}

	/** Normaliza respuestas `{ result: T }` o `T` plano. */
	private unwrapResult(resp: any): any {
		if (!resp || typeof resp !== 'object') {
			return resp;
		}
		if (resp.result && typeof resp.result === 'object' && !Array.isArray(resp.result)) {
			return resp.result;
		}
		return resp;
	}

	private parseJsonField(value: any): any {
		if (typeof value !== 'string') {
			return value;
		}
		try {
			return JSON.parse(value);
		} catch {
			return value;
		}
	}

	private resolveAnalysisErrorMessage(fallback: string, processPayload?: any): string {
		const rawError = processPayload?.error;
		const parsedResult = this.parseJsonField(processPayload?.result);
		const openaiError =
			parsedResult?.response?.data?.error ||
			parsedResult?.error ||
			null;

		const openaiMsg =
			openaiError?.message ||
			parsedResult?.message ||
			(typeof rawError === 'string' ? rawError : null);
		const openaiCode = openaiError?.code || null;
		const openaiType = openaiError?.type || null;
		const blob = [openaiMsg, rawError, JSON.stringify(parsedResult || '')]
			.filter(Boolean)
			.join(' ');
		const isQuota =
			openaiCode === 'insufficient_quota' ||
			openaiType === 'insufficient_quota' ||
			/insufficient_quota|exceeded your current quota/i.test(blob);

		if (isQuota) {
			return 'El análisis falló. Se notificó a Soporte.';
		}

		if (typeof openaiMsg === 'string' && openaiMsg.trim()) {
			return `El análisis falló: ${openaiMsg}`;
		}

		return fallback;
	}

	dismissAnalysisError() {
		this.analysisError = null;
	}

	dismissAnalysisProgress() {
		this.analysisProgress = null;
	}

	closeModal() {
		this.stopBackgroundPolling();
		this.activeModal.close('Modal Closed');
	}
	send() {
		if (this.prompt) {
			const payload = {
				id: this.requirementId,
				question: this.prompt
			}
			this.submitted = true;
			this.aiService.askQuestion(payload).subscribe({
				next: (resp: any) => {
					this.submitted = false;
					if(resp.error) {
						this.promptResult = '¡Ups! Hubo un error en la petición, por favor intenta mas tarde';
					} else {
						if (resp.result.success) {
							this.promptResult = resp.result.response;
						} else {
							this.promptResult = '¡Ups! Hubo un error en la petición, por favor intenta mas tarde';
						}
					}
				},
				error: (error) => {
					this.submitted = false;
					console.log(error);
				}
			});
		}
  	}

	copyToClipboard() {

		// Buscar el elemento que contiene el resultado del markdown
		const markdownResult = document.querySelector('.prompt-result');
	
		// Seleccionar el contenido del elemento y copiarlo al portapapeles
		if (markdownResult) {
			const range = document.createRange();
			range.selectNode(markdownResult);
			window.getSelection().removeAllRanges();
			window.getSelection().addRange(range);
			document.execCommand('copy');
		
			// Agregar la clase .show al span del tooltip para mostrarlo
			const tooltip = document.querySelector('.copy-tooltip');
			if (tooltip) {
				tooltip.classList.add('show');
				
				// Ocultar el tooltip después de 2 segundos
				setTimeout(() => {
					tooltip.classList.remove('show');
				}, 2000);
			}
		}
	}

	humanizeData(data: any) {
		// Si es string, intenta parsear como JSON
		if (typeof data === 'string') {
			try {
				data = JSON.parse(data);
			} catch {
				return data;
			}
		}

		// Si es un array vacío
		if (Array.isArray(data) && data.length === 0) {
			return 'Sin información';
		}

		// Si es un array con información
		if (Array.isArray(data)) {
			return data.map((item: any) => {
				// Si el elemento es un objeto, personaliza la visualización
				if (typeof item === 'object' && item !== null) {
					// Ejemplo: muestra los valores principales separados por espacio
					return Object.values(item).join(' ');
				}
				// Si es un valor primitivo, lo muestra tal cual
				return String(item);
			}).join(', ');
		}

		// Si no, retorna el JSON stringificado
		return JSON.stringify(data, null, 2);
	}
	  
	humanizeLabel(label: any) {
		// Convierte camelCase o PascalCase a palabras separadas por espacio
		if (typeof label !== 'string') return label;
		return label
			.replace(/([a-z])([A-Z])/g, '$1 $2') // Separa minúscula seguida de mayúscula
			.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Separa mayúsculas seguidas de minúscula
			.trim();
	}

	/**
	 * Poll de common.backgroundprocess hasta completed|failed.
	 * pending/processing → analysisProgress (mensaje de 2do plano).
	 * failed → analysisError (incluye insufficient_quota / credit_balance_exhausted).
	 */
	pollBackgroundProcessStatus(backgroundProcessId: number) {
		const maxAttempts = 60; // Máximo 60 intentos (5 minutos con intervalos de 5 segundos)
		let attempts = 0;
		const generation = this.pollGeneration;

		const scheduleNext = () => {
			if (generation !== this.pollGeneration) {
				return;
			}
			this.pollTimeoutId = setTimeout(checkStatus, 5000);
		};

		const checkStatus = () => {
			if (generation !== this.pollGeneration) {
				return;
			}

			attempts++;
			
			this.pollSubscription = this.aiService.getBackgroundProcess(backgroundProcessId).subscribe({
				next: (resp: any) => {
					if (generation !== this.pollGeneration) {
						return;
					}

					// Solo errores de transporte del routeReader: `{ error: { message } }`
					if (resp?.error && typeof resp.error === 'object') {
						this.setAnalysisError('No se pudo consultar el estado del análisis. Intenta de nuevo.');
						return;
					}

					const process = this.normalizeBackgroundProcess(resp);
					const status = process?.status;

					if (status === 'completed') {
						this.analysisProgress = null;
						this.processCompletedAnalysis();
					} else if (status === 'failed') {
						// process = { status, error, result }; result es text JSON con el error de OpenAI
						this.setAnalysisError(
							'El análisis falló. Por favor intenta de nuevo.',
							process
						);
					} else if (status === 'pending' || status === 'processing') {
						// Éxito / en curso: mostrar mensaje correcto, no error
						this.setAnalysisProgress(
							'El análisis se está procesando en segundo plano. El resultado se guardará automáticamente cuando esté listo.'
						);
						if (attempts < maxAttempts) {
							scheduleNext();
						} else {
							this.setAnalysisError(
								'El análisis está tomando más tiempo del esperado. Recarga la página más tarde.'
							);
						}
					} else {
						// Status desconocido o null: seguir esperando sin marcar error
						this.setAnalysisProgress(
							'El análisis se está procesando en segundo plano. El resultado se guardará automáticamente cuando esté listo.'
						);
						if (attempts < maxAttempts) {
							scheduleNext();
						} else {
							this.setAnalysisError('No se pudo determinar el estado del proceso de análisis.');
						}
					}
				},
				error: (error) => {
					if (generation !== this.pollGeneration) {
						return;
					}
					if (attempts < maxAttempts) {
						scheduleNext();
					} else {
						this.setAnalysisError(
							'Error al consultar el estado del análisis. Por favor intenta de nuevo.'
						);
						console.log(error);
					}
				}
			});
		};

		checkStatus();
	}

	processCompletedAnalysis() {
		// Obtener el requerimiento actualizado con el análisis completo
		this._requirements.getRequirementAllData(this.requirementId).subscribe({
			next: (resp: any) => {
				if (resp.error) {
					this.setAnalysisError('Error al obtener el resultado del análisis');
					return;
				}

				const requirement = resp.result;
				
				// Verificar si tiene airesponseobject con la respuesta
				if (requirement.airesponseobject && requirement.airesponseobject.output) {
					const response_text = requirement.airesponseobject.output.find(item => item.type === 'message');
					if (response_text) {
						const resume = JSON.parse(response_text.content[0].text);
						this.summaryData.summary = resume.resumen_analisis;
						const relevantDataObjectWithoutSummary = Object.entries(resume)
							.filter(([key]) => key !== 'resumen_analisis')
							.reduce((obj, [key, value]) => ({...obj, [key]: value}), {});
						this.summaryData.relevantData = relevantDataObjectWithoutSummary;
						this.aiService.syncRequirement({
							id: this.requirementId
						}).subscribe({
							next: (resp: any) => {
								console.log(resp);
							}
						});
					}
				} else if (requirement.airesume) {
					// Si solo tiene el resumen pero no el objeto completo
					this.summaryData.summary = requirement.airesume;
				}

				this.analisys_submitted = false;
				this.analysisError = null;
				this.analysisProgress = null;
			},
			error: (error) => {
				this.setAnalysisError('Error al obtener el resultado del análisis');
				console.log(error);
			}
		});
	}

	/**
	 * Flujo: processDocs → analyze (async) → poll backgroundProcessId.
	 * analyze success + backgroundProcessId NO es error: es procesamiento en 2do plano.
	 */
	generateAnalysis() {
		this.stopBackgroundPolling();
		this.analisys_submitted = true;
		this.analysisError = null;
		this.analysisProgress = null;
		this.aiService.processDocsRequirement({
			id: this.requirementId
		}).subscribe({
			next: (resp: any) => {
				// Solo `{ error: { message } }` del routeReader cuenta como fallo de transporte
				if (resp?.error && typeof resp.error === 'object') {
					this.setAnalysisError(
						'No se pudieron procesar los documentos para el análisis. Intenta de nuevo más tarde.'
					);
					return;
				}

				const processDocs = this.unwrapResult(resp);
				if (processDocs && processDocs.success === false) {
					this.setAnalysisError(
						processDocs.message ||
						'No se pudieron procesar los documentos para el análisis. Intenta de nuevo más tarde.'
					);
					return;
				}

				this.aiService.analyzeRequirement({
					id: this.requirementId
				}).subscribe({	
					next: (analyzeResp: any) => {
						if (analyzeResp?.error && typeof analyzeResp.error === 'object') {
							this.setAnalysisError(
								'No se pudo iniciar el análisis. Intenta de nuevo más tarde.'
							);
							return;
						}

						const analyzeResult = this.unwrapResult(analyzeResp);
						// Soporta wrap `{ result: { backgroundProcessId } }` o payload plano
						const backgroundProcessId =
							analyzeResult?.backgroundProcessId ??
							analyzeResp?.backgroundProcessId;

						if (analyzeResult && analyzeResult.success === false) {
							this.setAnalysisError(
								analyzeResult.message ||
								'No se pudo iniciar el análisis. Intenta de nuevo más tarde.'
							);
							return;
						}

						if (backgroundProcessId) {
							const progressMsg =
								analyzeResult?.message ||
								'El análisis se está procesando en segundo plano. El resultado se guardará automáticamente cuando esté listo.';
							this.setAnalysisProgress(progressMsg);
							this.pollBackgroundProcessStatus(Number(backgroundProcessId));
						} else {
							this.setAnalysisError(
								'No se recibió el ID del proceso en background. Por favor intenta de nuevo.'
							);
						}
					},
					error: (error) => {
						this.setAnalysisError(
							'No se pudo iniciar el análisis. Intenta de nuevo más tarde.'
						);
						console.log(error);
					}
				});
			},
			error: (error) => {
				this.setAnalysisError(
					'No se pudieron procesar los documentos para el análisis. Intenta de nuevo más tarde.'
				);
				console.log(error);
			}
		});
	}

}
